import React, { useState } from 'react';
import type { BreakpointDef } from '../Config/config';

interface GridSimulatorProps {
  breakpoints: BreakpointDef[];
  onSimulationChange: (simulatedWidth: number | undefined) => void;
  currentBreakpoint: BreakpointDef;
}

export const GridSimulator: React.FC<GridSimulatorProps> = ({
  breakpoints,
  onSimulationChange
                                                            }) => {
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedWidth, setSimulatedWidth] = useState<number>(768);
  const [selectedBreakpoint, setSelectedBreakpoint] = useState<string>('');

  const handleToggleSimulation = () => {
    if (isSimulating) {
      setIsSimulating(false);
      onSimulationChange(undefined);
    } else {
      setIsSimulating(true);
      onSimulationChange(simulatedWidth);
    }
  };

  const handleBreakpointSelect = (breakpointName: string) => {
    setSelectedBreakpoint(breakpointName);
    const bp = breakpoints.find(b => b.name === breakpointName);
    if (bp) {
      setSimulatedWidth(bp.minWidth);
      if (isSimulating) {
        onSimulationChange(bp.minWidth);
      }
    }
  };

  const handleCustomWidthChange = (width: number) => {
    setSimulatedWidth(width);
    setSelectedBreakpoint('custom');
    if (isSimulating) {
      onSimulationChange(width);
    }
  };

  // Déterminer le breakpoint simulé
  const simulatedBreakpoint = breakpoints.reduce((prev, curr) => {
    return simulatedWidth >= curr.minWidth ? curr : prev;
  }, breakpoints[0]);

  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      borderRadius: '6px',
      border: '1px solid rgba(0, 123, 255, 0.3)',
      marginBottom: '16px'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: '14px', 
          fontWeight: 'bold',
          color: '#007bff'
        }}>
          📱 Simulateur de Breakpoints
        </h4>
        
        <button
          onClick={handleToggleSimulation}
          style={{
            padding: '6px 12px',
            backgroundColor: isSimulating ? '#dc3545' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 'bold'
          }}
        >
          {isSimulating ? '✕ Arrêter' : '▶ Simuler'}
        </button>
      </div>

      {isSimulating && (
        <div style={{
          padding: '12px',
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '11px', 
              fontWeight: 'bold',
              marginBottom: '6px',
              color: '#28a745'
            }}>
              Breakpoint actuel : {simulatedBreakpoint.name.toUpperCase()} ({simulatedWidth}px)
            </label>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              marginBottom: '8px'
            }}>
              {breakpoints.map((bp) => (
                <button
                  key={bp.name}
                  onClick={() => handleBreakpointSelect(bp.name)}
                  style={{
                    padding: '6px 8px',
                    backgroundColor: selectedBreakpoint === bp.name ? '#007bff' : 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}
                >
                  {bp.name.toUpperCase()}<br/>
                  <span style={{ fontSize: '9px', opacity: 0.8 }}>
                    {bp.minWidth}px • {bp.columns} cols
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '10px', 
              marginBottom: '4px',
              opacity: 0.8
            }}>
              Largeur personnalisée :
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="range"
                min="320"
                max="1920"
                step="10"
                value={simulatedWidth}
                onChange={(e) => handleCustomWidthChange(Number(e.target.value))}
                style={{ flex: 1, height: '20px' }}
              />
              <input
                type="number"
                min="320"
                max="1920"
                value={simulatedWidth}
                onChange={(e) => handleCustomWidthChange(Number(e.target.value))}
                style={{
                  width: '80px',
                  padding: '4px 6px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '3px',
                  fontSize: '10px'
                }}
              />
              <span style={{ fontSize: '10px', opacity: 0.7 }}>px</span>
            </div>
          </div>

          <div style={{ 
            fontSize: '10px', 
            opacity: 0.7,
            padding: '8px',
            backgroundColor: 'rgba(40, 167, 69, 0.2)',
            borderRadius: '3px',
            border: '1px solid rgba(40, 167, 69, 0.3)'
          }}>
            💡 <strong>Conseil :</strong> Utilisez le simulateur pour tester votre interface sur différents appareils 
            sans redimensionner votre fenêtre de navigateur.
          </div>
        </div>
      )}

      {!isSimulating && (
        <div style={{ 
          fontSize: '11px', 
          opacity: 0.7,
          textAlign: 'center',
          padding: '8px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: '4px'
        }}>
          Cliquez sur "Simuler" pour tester votre grille sur différents breakpoints
        </div>
      )}
    </div>
  );
};

export default GridSimulator;


